import type { MasterFlightRecord } from '../../../utils/types/models';
import {
  formatDisplayDate,
  formatFrequencyWithUnderscores,
  getDisplayFrequency,
  getDisplayDate,
  formatTime,
  getRouteString,
} from '../../../utils/types/formatters';
import type { TimezoneMode } from '../../../contexts/TimezoneContext';
import { utcToMYT } from '../../../utils/timeUtils';

export interface ComparisonRow {
  fieldKey: string;
  label: string;
  values: string[];
  hasDiff: boolean;
  mono?: boolean;
}

export interface ComparisonResult {
  rows: ComparisonRow[];
  diffCount: number;
}

function valuesHaveDiff(values: string[]): boolean {
  return new Set(values).size > 1;
}

export function computeComparison(variants: MasterFlightRecord[], timezone: TimezoneMode = 'UTC'): ComparisonResult {
  if (variants.length === 0) return { rows: [], diffCount: 0 };

  const displayTime = (time: string) => {
    if (timezone === 'MYT') return formatTime(utcToMYT(time));
    return formatTime(time);
  };
  const tzLabel = timezone === 'MYT' ? 'MYT' : 'UTC';

  const rows: ComparisonRow[] = [];

  // --- Flight-level fields ---
  const flightFields: { key: string; label: string; extract: (v: MasterFlightRecord) => string; mono?: boolean }[] = [
    { key: 'season', label: 'Season', extract: v => v.seasonId || '—' },
    { key: 'status', label: 'Status', extract: v => v.scheduleStatus || '—' },
    { key: 'serviceType', label: 'Service Type', extract: v => v.iataServiceTypeCode || '—' },
    { key: 'startDate', label: 'Start Date', extract: v => formatDisplayDate(getDisplayDate(v.startDate, v.stations?.[0]?.std, timezone)) },
    { key: 'endDate', label: 'End Date', extract: v => formatDisplayDate(getDisplayDate(v.endDate, v.stations?.[0]?.std, timezone)) },
    { key: 'frequency', label: 'Frequency', extract: v => formatFrequencyWithUnderscores(getDisplayFrequency(v.frequency, v.frequencyMYT, timezone)), mono: true },
    { key: 'freqRate', label: 'Freq Rate', extract: v => v.freqRate || 'Weekly' },
    { key: 'opSuffix', label: 'Op. Suffix', extract: v => v.operationalSuffix || '—', mono: true },
    { key: 'route', label: 'Route', extract: v => getRouteString(v.stations) },
    { key: 'sectorCount', label: 'Sectors', extract: v => String(v.stations?.length || 0) },
  ];

  for (const field of flightFields) {
    const values = variants.map(field.extract);
    rows.push({
      fieldKey: field.key,
      label: field.label,
      values,
      hasDiff: valuesHaveDiff(values),
      mono: field.mono,
    });
  }

  // --- Per-sector fields ---
  const maxSectors = Math.max(...variants.map(v => v.stations?.length || 0));

  for (let i = 0; i < maxSectors; i++) {
    const sectorLabel = `Sector ${i + 1}`;

    const sectorFields: { key: string; label: string; extract: (v: MasterFlightRecord) => string; mono?: boolean }[] = [
      {
        key: `s${i}_route`,
        label: `${sectorLabel} Route`,
        extract: v => {
          const s = v.stations?.[i];
          return s ? `${s.departureStation} → ${s.arrivalStation}` : '—';
        },
      },
      {
        key: `s${i}_std`,
        label: `${sectorLabel} STD (${tzLabel})`,
        extract: v => displayTime(v.stations?.[i]?.std || ''),
        mono: true,
      },
      {
        key: `s${i}_sta`,
        label: `${sectorLabel} STA (${tzLabel})`,
        extract: v => displayTime(v.stations?.[i]?.sta || ''),
        mono: true,
      },
      {
        key: `s${i}_aircraft`,
        label: `${sectorLabel} Aircraft`,
        extract: v => v.stations?.[i]?.iataSubTypeCode || '—',
      },
      {
        key: `s${i}_config`,
        label: `${sectorLabel} Config`,
        extract: v => v.stations?.[i]?.aircraftConfiguration || '—',
        mono: true,
      },
      {
        key: `s${i}_termDep`,
        label: `${sectorLabel} Term Dep`,
        extract: v => v.stations?.[i]?.passengerTerminalDep || '—',
      },
      {
        key: `s${i}_termArr`,
        label: `${sectorLabel} Term Arr`,
        extract: v => v.stations?.[i]?.passengerTerminalArr || '—',
      },
      ...(timezone === 'UTC' ? [
        {
          key: `s${i}_utcDep`,
          label: `${sectorLabel} UTC Dep`,
          extract: (v: MasterFlightRecord) => v.stations?.[i]?.utcLocalTimeVariationDep || '—',
          mono: true,
        },
        {
          key: `s${i}_utcArr`,
          label: `${sectorLabel} UTC Arr`,
          extract: (v: MasterFlightRecord) => v.stations?.[i]?.utcLocalTimeVariationArr || '—',
          mono: true,
        },
      ] : []),
      {
        key: `s${i}_dayChange`,
        label: `${sectorLabel} Day Change`,
        extract: v => {
          const s = v.stations?.[i];
          return s ? `${s.cd ?? 0} / ${s.ca ?? 0}` : '—';
        },
      },
      {
        key: `s${i}_owner`,
        label: `${sectorLabel} Aircraft Owner`,
        extract: v => v.stations?.[i]?.aircraftOwner || '—',
      },
      {
        key: `s${i}_tailNo`,
        label: `${sectorLabel} Tail No`,
        extract: v => v.stations?.[i]?.tailNo || '—',
        mono: true,
      },
      {
        key: `s${i}_onward`,
        label: `${sectorLabel} Onward Flight`,
        extract: v => v.stations?.[i]?.onwardFlight || '—',
        mono: true,
      },
      {
        key: `s${i}_codeshares`,
        label: `${sectorLabel} Codeshares`,
        extract: v => {
          const cs = v.stations?.[i]?.codeshareFlightNo;
          return cs && cs.length > 0 ? cs.join(', ') : '—';
        },
      },
    ];

    for (const field of sectorFields) {
      const values = variants.map(field.extract);
      rows.push({
        fieldKey: field.key,
        label: field.label,
        values,
        hasDiff: valuesHaveDiff(values),
        mono: field.mono,
      });
    }
  }

  const diffCount = rows.filter(r => r.hasDiff).length;
  return { rows, diffCount };
}
